import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  HiOutlineAcademicCap,
  HiOutlineDocumentDownload,
  HiOutlineRefresh,
  HiOutlineUserGroup,
} from "react-icons/hi";
import Header from "components/common/Header/Header";
import SideBarNew from "components/common/SideBar/SideBarNew";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { LoginContext } from "helpers/LoginContext";
import { classNames } from "Utils/utils";
import { toast } from "react-toastify";
import spApi from "store/services/sp.services";
import WyzedConnectModal from "./WyzedConnectModal";
import WyzedWorkerMapping from "./WyzedWorkerMapping";
import ModalSkeleton from "components/Modals/ModalSkeleton";

const Integrations = () => {
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await spApi.getWyzedStatus();
      setStatus(response?.data ?? { connected: false });
    } catch (err) {
      toast.error(err?.message ?? "Failed to load integration status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await spApi.syncWyzed();
      const synced = response?.data?.synced_workers ?? 0;
      const disconnected = response?.data?.disconnected_workers ?? 0;
      toast.success(
        `Synchronised training for ${synced} worker${synced === 1 ? "" : "s"}.` +
          (disconnected > 0
            ? ` ${disconnected} link${disconnected === 1 ? "" : "s"} cleared because the learner no longer exists in Wyzed.`
            : "")
      );
      loadStatus();
    } catch (err) {
      toast.error(err?.message ?? "Synchronisation failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await spApi.disconnectWyzed();
      toast.success("Wyzed disconnected.");
      setDisconnectOpen(false);
      loadStatus();
    } catch (err) {
      toast.error(err?.message ?? "Failed to disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = status?.connected === true;

  const wyzedFeatures = [
    {
      icon: HiOutlineUserGroup,
      title: "Map workers to learners",
      description: "Link Vertex360 workers to Wyzed accounts individually or in bulk.",
    },
    {
      icon: HiOutlineRefresh,
      title: "Sync training progress",
      description: "Pull course status and completion from Wyzed into worker profiles.",
    },
    {
      icon: HiOutlineDocumentDownload,
      title: "Download certificates",
      description: "Access completed course certificates without leaving Vertex360.",
    },
  ];

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={classNames(
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300",
          "flex flex-col"
        )}
      >
        <main className="min-h-screen flex-1 bg-white">
          <Header title="Integrations" />
          {loading ? (
            <div className="flex justify-center items-center min-h-[50vh]">
              <CircularLoader classes="flex justify-center" classloader="loader-l" />
            </div>
          ) : (
            <>
              <div className="sm:px-6 lg:px-8 py-4">
                <p className="font-bold text-xl">Integrations</p>
                <p className="text-sm text-gray-500 mt-1">
                  Connect third-party services to synchronise data with Vertex360.
                </p>
              </div>

              <div className="sm:px-6 lg:px-8 pb-8">
                <div className="bg-gray-50 px-5 py-4 rounded-lg">
                  {connected ? (
                    <div className="grid gap-3 lg:grid-cols-3 md:grid-cols-1">
                      <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div
                            className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-md text-white"
                            style={{ backgroundColor: "#1a56db" }}
                          >
                            <HiOutlineAcademicCap className="w-8 h-8" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Wyzed</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Synchronise worker training, progress and certificates from
                              Wyzed.
                            </p>
                            <p className="text-xs text-green-700 mt-2">
                              Connected · Tenant {status?.wyzed_tenant_id}
                              {status?.last_synced_at && (
                                <>
                                  {" "}
                                  · Last sync{" "}
                                  {new Date(status.last_synced_at).toLocaleString()}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2 justify-end">
                          <CustomButton
                            label="Synchronise"
                            showLoading={syncing}
                            isDisabled={syncing || isUserGracePeriodOver}
                            clickHandler={handleSync}
                          />
                          <CustomButton
                            label="Disconnect"
                            variant="danger"
                            isDisabled={disconnecting || isUserGracePeriodOver}
                            showLoading={disconnecting}
                            clickHandler={() => setDisconnectOpen(true)}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <div className="border-b border-slate-100 bg-slate-50 px-6 py-8">
                        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-start gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm">
                              <HiOutlineAcademicCap className="h-8 w-8" />
                            </div>
                            <div>
                              <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                                Not connected
                              </span>
                              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                                Wyzed LMS
                              </h2>
                              <p className="mt-1 max-w-xl text-sm text-slate-600">
                                Connect Wyzed to view worker training, sync course progress,
                                and download certificates from Vertex360.
                              </p>
                            </div>
                          </div>
                          <CustomButton
                            label="Connect Wyzed"
                            isDisabled={isUserGracePeriodOver}
                            clickHandler={() => setConnectOpen(true)}
                          />
                        </div>
                      </div>

                      <div className="grid gap-6 px-6 py-6 md:grid-cols-3">
                        {wyzedFeatures.map(({ icon: Icon, title, description }) => (
                          <div
                            key={title}
                            className="rounded-lg border border-slate-100 bg-slate-50/50 p-4"
                          >
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600">
                              <Icon className="h-5 w-5" />
                            </div>
                            <p className="mt-3 text-sm font-semibold text-slate-900">
                              {title}
                            </p>
                            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                              {description}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-slate-100 bg-slate-50/80 px-6 py-4">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium text-slate-800">Getting started:</span>{" "}
                          In Wyzed, go to Account Settings → Integrations → Install Wyzed
                          API, then paste your credentials in the connect dialog.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {connected && <WyzedWorkerMapping onSaved={loadStatus} />}
              </div>
            </>
          )}
        </main>
      </div>

      <WyzedConnectModal
        isOpen={connectOpen}
        closeModal={() => setConnectOpen(false)}
        onConnected={loadStatus}
      />

      <ModalSkeleton
        isOpen={disconnectOpen}
        cssClass="w-[30%]"
        title="Disconnect Wyzed"
        closeModal={() => setDisconnectOpen(false)}
        modalFooter={
          <>
            <CustomButton label="Cancel" clickHandler={() => setDisconnectOpen(false)} />
            <CustomButton
              label="Disconnect"
              variant="danger"
              showLoading={disconnecting}
              isDisabled={disconnecting}
              clickHandler={handleDisconnect}
            />
          </>
        }
      >
        <p className="text-sm text-gray-600">
          This removes the Wyzed connection and cached training data for your
          organisation. You can reconnect at any time.
        </p>
      </ModalSkeleton>
    </div>
  );
};

export default Integrations;
